/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { AccountService } from 'app/core/services/account-service';
import { State, IForm } from 'app/shared/lib/essette-site';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { Toaster } from 'app/shared/lib/toaster';
import * as Core from 'app/core/models/essette-api';
import { FeatureService } from 'app/shared/services/feature-service';
import { FeatureKey } from 'app/shared/models/feature-set';

@autoinject
export class MyAccountPage {

    protected account: Core.AccountContext = {};
    protected edit: Core.AccountContext | null = null;

    protected $isValid: boolean = true;
    protected validateForm = validate;

    protected readonly $modal!: IPopup;
    protected readonly $pwModal!: IPopup;
    protected readonly $changePassword!: IForm;

    private readonly state: State = new State();

    private sessionEvent: Subscription | null = null;

    constructor(
        private readonly env: EnvironmentService,
        private readonly log: AppLogger,
        private readonly events: EventAggregator,
        private readonly accountService: AccountService,
        private readonly toaster: Toaster,
        protected readonly featureService: FeatureService) {
    }

    protected activate(): void {
        this.loadAccount();
    }

    protected attached(): void {
        this.sessionEvent = this.events.subscribe(SessionChangedEvent,
            (e: SessionChangedEvent) => {
                if(e.key === 'account') {
                    this.loadAccount();
                }
            });
    }

    protected detached(): void {
        this.sessionEvent?.dispose();
    }

    protected toggleFeature(feature: FeatureKey) {
        this.featureService.toggleFeature(feature);
    }

    protected formatUtcOffset(offset: number): string {
        const sign = Math.sign(offset) < 0 ? '-' : '+';
        const abs = Math.abs(offset);
        let hours = `${Math.trunc(abs / 60)}`;
        if(hours.length < 2) { hours = '0' + hours; };
        let minutes = `${Math.trunc(abs % 60)}`;
        if(minutes.length < 2) { minutes = '0' + minutes; };
        return `UTC${sign}${hours}:${minutes}`;
    }

    protected accountSaved(event: FormSuccessCustomEvent) {
        this.closeModal(event.detail.form);
        this.loadAccount();
    }

    protected passwordChanged(event: FormSuccessCustomEvent) {
        this.toaster.info().message({ key: 'ui:messages.password.changeSuccessful' }).show();
        this.closeModal(event.detail.form);
    }

    protected async editAccount(): Promise<void> {
        this.edit = deepClone(this.account);
        this.$modal.open();
    }

    protected changePassword(): void {
        if(this.$changePassword.reset) {
            this.$changePassword.reset();
        }
        this.$pwModal.open();
    }

    private closeModal(form: IForm): void {
        this.$modal.close();
        this.$pwModal.close();
        if(form.reset) {
            form.reset();
        }
    }

    private async loadAccount(): Promise<void> {
        if(!this.env.context.id) {
            this.account = {};
            return;
        }
        try {
            this.state.beginLoad();

            const includes = ['user', 'user.phoneNumber.primary', 'user.phoneNumber.cell', 'user.phoneNumber.fax', 'user.displayTimeZone', 'homePage'];
            const response = await this.accountService.loadAccountContext(this.env.context.id, includes);
            if(response.isSuccessful && response.results.length > 0) {
                this.account = response.results[0];
            }
        }
        catch(error) {
            this.log.error('Error Loading Account', error);
        }
        finally {
            this.state.endLoad();
        }
    }

}
